// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to fetch users in an organization from the remote API.
 */
import { OrganizationUser, organizationUserSchema } from '@/account/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { z } from 'zod';

export const userIdentifierSchema = z.object({
  id: z.string(),
  key: z.string(),
});

export type UserIdentifier = z.infer<typeof userIdentifierSchema>;

/**
 * Type representing a single user in the organization
 */
export type User = OrganizationUser;

/**
 * Schema for validating the entire API response
 */
const getOrganizationUsersResponseSchema = z
  .object({
    data: z.array(organizationUserSchema).nullable(),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    users: data ?? [],
  }));

/**
 * Type representing the API response
 */
type GetOrganizationUsersResponse = z.infer<
  typeof getOrganizationUsersResponseSchema
>;

/**
 * Fetches all users in an organization
 * @param organizationKey - The unique key of the organization
 * @returns A list of users in the organization
 * @throws Will throw an error if the API request fails or returns invalid data
 */
export async function getOrganizationUsers(
  organizationKey: string
): Promise<GetOrganizationUsersResponse> {
  const path = `/v1/organization/${organizationKey}/users`;
  const response = await api.get(path);

  return parseResponse(getOrganizationUsersResponseSchema, response.data, {
    schemaName: 'OrganizationUsers',
    endpoint: path,
  });
}
